// eslint-disable-next-line eag/no-external-date-time-format-functions -- Must byte-match the RFC3339 model DateTimeRangeFilter stores
import {endOfDay, formatRFC3339, parseISO, startOfDay} from 'date-fns';

import {isNilOrEmpty} from 'ramda-adjunct';

import {FILTER_VALUE_KEY} from '../constants/filterValueKey';
import type {EagColumn, EagFilterDefinition} from '../types/EagColumn';
import type {FilterState} from '../types/FilterState';
import {toPlainLabel} from './toPlainLabel';

type RawCell = {value: unknown} | null | undefined;

const MAX_LABEL_LENGTH = 30;

/**
 * Human-readable display text of the clicked cell for the "Filter by: {value}"
 * menu label — toPlainLabel's rendering (object `.label`, arrays joined,
 * localized Yes/No) truncated to {@link MAX_LABEL_LENGTH} chars. Returns `null`
 * for empty/unlabelable cells so the menu falls back to the plain
 * "Filter by value" text.
 */
export const buildFilterByValueLabel = (rawCell: unknown): string | null => {
  const cellValue = (rawCell as RawCell)?.value;
  const money =
    typeof cellValue === 'object' && cellValue !== null
      ? (cellValue as Record<string, unknown>)
      : null;
  const label =
    money?.amount != null
      ? [String(money.amount), money.currency].filter(Boolean).join(' ')
      : toPlainLabel(cellValue);
  if (label === '') {
    return null;
  }
  return label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…` : label;
};

const MULTI_OPTION_TYPES = ['MULTI_CHOICE', 'MULTI_CHOICE_ASYNC', 'CHIPS', 'CHECKBOX'] as const;
const NUMERIC_TYPES = ['RANGE', 'SLIDER', 'SLIDER_RANGE', 'FILE_SIZE_SLIDER'] as const;

/** Numeric value of a cell: a number, a numeric string, or a Money `{amount}` object. */
const toCellNumber = (cellValue: unknown): number | null => {
  const candidate =
    typeof cellValue === 'object' && cellValue !== null
      ? (cellValue as Record<string, unknown>).amount
      : cellValue;
  if (typeof candidate !== 'number' && (typeof candidate !== 'string' || candidate === '')) {
    return null;
  }
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

/** 'YYYY-MM-DD' day of a date/datetime string cell, or null when it is not one. */
const toCellDay = (cellValue: unknown): string | null =>
  typeof cellValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(cellValue)
    ? cellValue.slice(0, 10)
    : null;

/** True when the value sits outside the filter definition's min/max bounds. */
const isOutOfBounds = (
  filter: EagFilterDefinition,
  value: number | string,
  normalize: (bound: number | string) => number | string
): boolean => {
  const min = filter.min != null ? normalize(filter.min) : null;
  const max = filter.max != null ? normalize(filter.max) : null;
  return (min != null && value < min) || (max != null && value > max);
};

/**
 * Extracts the filterable option key(s) from a multi-option cell value:
 * `{key, label}` objects (ENUM/CODE_LIST), `{id, label}` objects (suggestion
 * entities), arrays of either (isArray columns), or a bare primitive key.
 */
const toOptionKeys = (cellValue: unknown): string[] => {
  if (Array.isArray(cellValue)) {
    return cellValue.flatMap(toOptionKeys);
  }
  if (typeof cellValue === 'object' && cellValue !== null) {
    const record = cellValue as Record<string, unknown>;
    const key = record.key ?? record.id;
    return key == null ? [] : [String(key)];
  }
  if (typeof cellValue === 'string' || typeof cellValue === 'number') {
    return [String(cellValue)];
  }
  return [];
};

export type FilterByValueAction = {
  /** 'add' applies the cell value to the filter; 'remove' takes it back out (toggle). */
  mode: 'add' | 'remove';
  /** Wrapped `{value: X}` filter state to hand to handleFilterChange. An inactive
   * payload (`{value: null}` / `{value: []}`) makes handleFilterChange delete the
   * filter key entirely — that is how "remove the last value" clears the filter. */
  payload: FilterState;
};

/**
 * Builds the toggle action for the context menu's "Filter by value" item from
 * the clicked cell's raw `{value, style}` wrapper. Returns `null` when the item
 * should be hidden: the column is not filterable, the cell is empty, the cell
 * value does not fit the filter type (e.g. non-numeric cell on a slider), the
 * value falls outside the filter's min/max bounds, or a stepped slider has no
 * step at the value.
 *
 * Multi-option types APPEND to the current selection (checkbox semantics,
 * matching the filter widgets); once EVERY cell key is already selected the
 * action flips to 'remove' and strips the cell keys from the selection. TEXT
 * and SWITCH hold one value: 'add' overwrites, and an exact match flips to
 * 'remove' which clears the filter. Numeric ranges/sliders pin the exact value
 * ({from,to} object for RANGE, [from,to] tuple for the slider types, bare
 * number for SLIDER); DATE/DATE_RANGE pin the exact day; DATE_TIME_RANGE pins
 * the timestamp's whole day window. All of them toggle to 'remove' (clearing
 * the filter) when the current value is already that exact pin.
 */
// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export const buildFilterByValueAction = (
  column: EagColumn,
  rawCell: unknown,
  currentFilterState: FilterState | undefined
): FilterByValueAction | null => {
  const filter = column.filter;
  if (!filter || filter.isDisabled) {
    return null;
  }

  const cellValue = (rawCell as RawCell)?.value;
  if (isNilOrEmpty(cellValue) && cellValue !== 0) {
    return null;
  }

  if (filter.type === 'TEXT') {
    const text =
      typeof cellValue === 'object' && cellValue !== null // eslint-disable-line no-nested-ternary -- Pre-existing violation
        ? ((cellValue as Record<string, unknown>).label ?? null)
        : typeof cellValue === 'string' || typeof cellValue === 'number'
          ? cellValue
          : null;
    if (isNilOrEmpty(text) && text !== 0) {
      return null;
    }
    if (currentFilterState?.[FILTER_VALUE_KEY] === String(text)) {
      return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: String(text)}};
  }

  if (filter.type === 'SWITCH') {
    if (cellValue !== true) {
      return null;
    }
    if (currentFilterState?.[FILTER_VALUE_KEY] === true) {
      return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: true}};
  }

  if (MULTI_OPTION_TYPES.includes(filter.type as (typeof MULTI_OPTION_TYPES)[number])) {
    const cellKeys = toOptionKeys(cellValue);
    if (cellKeys.length === 0) {
      return null;
    }
    const currentValue = currentFilterState?.[FILTER_VALUE_KEY];
    const currentKeys = Array.isArray(currentValue) // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? currentValue.map(String)
      : typeof currentValue === 'string'
        ? [currentValue]
        : [];
    const newKeys = cellKeys.filter((key) => !currentKeys.includes(key));
    if (newKeys.length === 0) {
      return {
        mode: 'remove',
        payload: {[FILTER_VALUE_KEY]: currentKeys.filter((key) => !cellKeys.includes(key))},
      };
    }
    // multiple: false renders a single-select Choice (renderFilter isMulti) —
    // the model stays an array but must hold ONE key, so replace instead of
    // append, and hide for multi-key array cells it cannot express.
    if (filter.multiple === false) {
      return cellKeys.length === 1 ? {mode: 'add', payload: {[FILTER_VALUE_KEY]: cellKeys}} : null;
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: [...currentKeys, ...newKeys]}};
  }

  if (NUMERIC_TYPES.includes(filter.type as (typeof NUMERIC_TYPES)[number])) {
    const numeric = toCellNumber(cellValue);
    if (
      numeric == null ||
      isOutOfBounds(filter, numeric, Number) ||
      // Sliders with discrete steps can only sit on a step — a value off the
      // scale would render a broken thumb, so the item hides instead.
      (isNilOrEmpty(filter.steps) ? false : !filter.steps?.includes(numeric))
    ) {
      return null;
    }
    const current = currentFilterState?.[FILTER_VALUE_KEY];
    if (filter.type === 'SLIDER') {
      if (current === numeric) {
        return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
      }
      return {mode: 'add', payload: {[FILTER_VALUE_KEY]: numeric}};
    }
    // RANGE, SLIDER_RANGE and FILE_SIZE_SLIDER all STORE a {from, to} object —
    // the slider widgets only work with a [from, to] tuple internally
    // (useSliderFilter converts on write). unwrapFilterValues ships the stored
    // value verbatim to createDataQuery, whose schema only accepts the object.
    const interval = current as {from?: unknown; to?: unknown} | null | undefined;
    if (interval?.from === numeric && interval?.to === numeric) {
      return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: {from: numeric, to: numeric}}};
  }

  if (filter.type === 'DATE' || filter.type === 'DATE_RANGE') {
    const day = toCellDay(cellValue);
    if (day == null || isOutOfBounds(filter, day, (bound) => String(bound).slice(0, 10))) {
      return null;
    }
    const current = currentFilterState?.[FILTER_VALUE_KEY];
    if (filter.type === 'DATE') {
      if (current === day) {
        return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
      }
      return {mode: 'add', payload: {[FILTER_VALUE_KEY]: day}};
    }
    const interval = current as {from?: unknown; to?: unknown} | null | undefined;
    if (interval?.from === day && interval?.to === day) {
      return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: {from: day, to: day}}};
  }

  if (filter.type === 'DATE_TIME_RANGE') {
    if (toCellDay(cellValue) == null) {
      return null;
    }
    const parsed = parseISO(cellValue as string);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    // "Exactly this value" for a timestamp means its whole day — an exact-second
    // window would silently miss rows whose stored precision differs from the
    // rendered one. Same RFC3339 formatting as DateTimeRangeFilter.
    const from = formatRFC3339(startOfDay(parsed));
    const to = formatRFC3339(endOfDay(parsed));
    const interval = currentFilterState?.[FILTER_VALUE_KEY] as
      {from?: unknown; to?: unknown} | null | undefined;
    if (interval?.from === from && interval?.to === to) {
      return {mode: 'remove', payload: {[FILTER_VALUE_KEY]: null}};
    }
    return {mode: 'add', payload: {[FILTER_VALUE_KEY]: {from, to}}};
  }

  return null;
};
